import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Mail, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GITES, HERO_IMG, BRAND } from "@/data";
import { EN, BOOK_TOGETHER } from "@/en";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1, ease: "easeOut" } }),
};

const EN_PATH = { "chez-anne": "/en/chez-anne", "au-19": "/en/au-19" };

export default function HomeEn() {
  const t = EN.home;
  return (
    <div data-testid="page-home-en">
      <section data-testid="home-hero-en" className="relative min-h-[90vh] flex items-center">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="The terrace with a view over the valley" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-forest-dark/85 via-forest-dark/45 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full py-28">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="max-w-2xl">
            <h1 className="font-serif font-light text-cream text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
              {t.heroTitle[0]}<br />{t.heroTitle[1]}<br />{t.heroTitle[2]}
            </h1>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/en/cottages" data-testid="hero-cta-cottages-en">
                <Button className="rounded-full bg-cream hover:bg-white text-forest px-8 h-12 text-base">
                  {t.ctaCottages} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <a href={BRAND.emailLink} data-testid="hero-cta-email-en">
                <Button variant="outline" className="rounded-full border-cream/60 text-cream bg-white/10 hover:bg-cream hover:text-forest px-8 h-12 text-base">
                  <Mail className="mr-2 w-4 h-4" /> {t.ctaEmail}
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <section data-testid="home-editorial-en" className="py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
            <span className="text-xs font-semibold uppercase tracking-[0.28em] text-terracotta">{t.overline}</span>
            <h2 className="font-serif text-4xl lg:text-5xl font-light mt-5 leading-tight text-forest">{t.editorialTitle}</h2>
            <p className="text-charcoal/70 text-lg leading-relaxed mt-8">{t.editorialText}</p>
            <p className="text-charcoal/60 mt-6 max-w-xl">{t.subtitle}</p>
          </motion.div>
        </div>
      </section>

      <section id="cottages" data-testid="home-cottages-en" className="pb-16 lg:pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-8">
          {GITES.map((g, i) => (
            <motion.div key={g.id} data-testid={`home-cottage-${g.id}-en`} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}>
              <Link to={EN_PATH[g.id]} className="group block">
                <div className="overflow-hidden rounded-2xl">
                  <img src={g.cover} alt={g.name} className="w-full h-[380px] object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <h3 className="font-serif text-3xl text-forest mt-6">{g.name}</h3>
                <p className="text-charcoal/70 mt-2 leading-relaxed">{EN.cottages[g.id].tagline}</p>
                <span className="inline-flex items-center gap-2 mt-4 text-forest text-sm font-medium group-hover:gap-3 transition-all">
                  {t.viewCottage} <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
              <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
                <a href={g.booking} target="_blank" rel="noopener noreferrer" data-testid={`home-booking-${g.id}-en`}>
                  <Button className="rounded-full bg-forest hover:bg-forest-dark text-cream px-6">
                    {EN.house.bookCottage(g.name)} <ExternalLink className="ml-2 w-4 h-4" />
                  </Button>
                </a>
                <a href={g.booking} target="_blank" rel="noopener noreferrer" data-testid={`home-reviews-${g.id}-en`} className="text-sm text-forest underline underline-offset-4 hover:text-forest-dark">
                  {EN.house.reviews}
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section data-testid="home-booktogether-en" className="pb-24 lg:pb-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="rounded-2xl bg-stone/50 border border-sand p-8 lg:p-10">
            <p className="text-charcoal/80 leading-relaxed">{BOOK_TOGETHER}</p>
            <a href={BRAND.emailLink} data-testid="booktogether-email-en" className="inline-flex items-center gap-2 mt-5 text-forest font-medium hover:gap-3 transition-all">
              <Mail className="w-4 h-4" /> {EN.home.ctaEmail}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
